// src/pages/usa/colorado/BuenaVista.js
import "../../../App.css";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import BackOnly from "../../../components/BackOnly";
import ContactPopup from "../../../components/ContactPopup";
import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

import AffiliateBooking from "../../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../../components/affiliate/AffiliateTrip";

// ⭐ STRICT COMMAND: Buena Vista ALWAYS goes back to Colorado map
export const backRoute = "/map/colorado";   
// or "/travel/usa/colorado" if THAT is your actual Colorado map route

export default function BuenaVista() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly (NO LOOPS)
  useEffect(() => {
    if (!window.__buenaVistaBackRouteSet) {
      window.__buenaVistaBackRouteSet = true;
      navigate(".", { replace: true, state: { backRoute } });
    }
  }, [navigate]);

  return (
    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>


      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Buena Vista, Colorado</h1>

        <section className="travel-review">
          {/* LEFT COLUMN — TEXT */}
          <div className="travel-text">
            <h2>🏞️ Discover Buena Vista, Colorado</h2>

            <p>
              Buena Vista is nestled in the heart of the Arkansas River Valley, surrounded by the towering peaks of the
              Sawatch Range. This small mountain town offers stunning views, a welcoming vibe, and easy access to some
              of the most iconic hikes in the state.
            </p>

            <p>
              It’s also home to several of Colorado’s famous 14ers — mountains rising above 14,000 feet — including
              Mount Princeton, Mount Yale, and Mount Harvard. Whether you're here to summit a peak, soak in the hot
              springs, or enjoy a local brew after a long hike, Buena Vista is a perfect base for high‑country
              adventure.
            </p>

            <h3>🚗 Getting There</h3>

            <p>
              The drive from Denver to Buena Vista takes about 2 hours and 15 minutes under typical conditions. The
              route winds through scenic mountain passes, especially if you take US‑285.
            </p>

            <p>It’s a beautiful drive, but plan for extra time during peak hours or winter weather.</p>

            {/* 🌟 MIDLAND TUNNELS */}
            <h3>🌟 Midland Tunnels</h3>

            <p>
              Even though the Midland Tunnels technically fall outside the official limits of Buena Vista, their
              location and proximity have made them a beloved cultural reference for locals and visitors alike.
              Originally carved out by the Colorado Midland Railway in the late 1800s, these tunnels now serve as a
              scenic drive and a quiet reminder of the region’s mining and rail history.
            </p>

            <p>
              Locals often point travelers toward them as a must‑see attraction — whether you're chasing ghost town
              vibes, historic routes, or just a unique photo op along County Road 371.
            </p>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Midland Tunnels:</strong> Scenic historic tunnels along County Road 371.<br />
                  <a
                    href="https://www.google.com/maps?q=38.875993969060644,-106.14552762374645"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Midland Tunnels
                  </a>
                </div>
              </li>
            </ul>

            {/* 🥾 OTHER HIDDEN GEMS */}
            <h3>🥾 Other Hidden Gems Around Buena Vista</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>St. Elmo Ghost Town:</strong> One of Colorado’s best‑preserved ghost towns.<br />
                  <a
                    href="https://www.google.com/maps?q=38.703525147255846,-106.3456539488864"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 St. Elmo Ghost Town
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Whitewater Rafting on the Arkansas River:</strong> From family floats to Class IV thrills.<br />
                  <a
                    href="https://www.google.com/maps?q=38.84669178155388,-106.12222625910462"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Whitewater Rafting
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Cottonwood Pass:</strong> Scenic drive with panoramic views and alpine lakes.<br />
                  <a
                    href="https://www.google.com/maps?q=38.827641429454154,-106.40678387136116"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Cottonwood Pass
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Mount Princeton Hot Springs:</strong> Natural soaking pools with mountain views.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>14ers in the Collegiate Peaks:</strong> Mount Princeton, Mount Yale, and Mount Harvard.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Deerhammer Distillery:</strong> Small‑batch whiskey with a BV soul.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Midland Hill Trail:</strong> A locals’ favorite for sunrise hikes.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <em>
                    Note for hikers: If you're not familiar with high altitude or you're not a regular hiker, prepare
                    before attempting a 14er. These peaks can take 4–10+ hours and require water, layers, and weather
                    awareness.
                  </em>
                </div>
              </li>
            </ul>

            {/* 🛏️ PLACES TO STAY */}
            <h3>🛏️ Places to Stay in Buena Vista</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Surf Hotel & Chateau:</strong> Boutique luxury on the Arkansas River.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Mt. Princeton Hot Springs Resort:</strong> Creekside soaking pools.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Best Western Vista Inn:</strong> Reliable comfort with mountain views.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Buffalo Peaks Lodge:</strong> Quaint B&B with cozy rooms.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Topaz Lodge:</strong> Budget‑friendly and central.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>The Inn:</strong> Boutique charm with modern touches.
                </div>
              </li>
            </ul>

            {/* AFFILIATE BLOCK */}
            <h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
                        <div className="affiliate-block">
                          <AffiliateExpedia />
                          <AffiliateBooking />
                          <AffiliateTrip />
                        </div>

            {/* 🎥 VIDEOS */}
            <h3>🎥 My Travel Videos</h3>

            <p>Here is a video I took from the Midland Tunnels:</p>

            <p>
              <a
                href="https://youtu.be/i41lO5oxgXk?si=RoAx2m9OmKWUi6lh"
                target="_blank"
                rel="noopener noreferrer"
              >
                Midland Tunnels
              </a>
            </p>
          </div>

          {/* RIGHT COLUMN — IMAGES */}
          <div className="travel-image-column">
            <a
              href="https://youtu.be/i41lO5oxgXk?si=RoAx2m9OmKWUi6lh"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/colorado/buenavista/midlandtunnels.jpg"
                alt="Midland Tunnel Video"
              />
            </a>

            <img src="/images/usa/colorado/buenavista/road.jpg" alt="The Road" loading="lazy" />
            <img src="/images/usa/colorado/buenavista/driving.jpg" alt="Driving" loading="lazy" />
            <img src="/images/usa/colorado/buenavista/lakeintown.jpg" alt="Lake in town" loading="lazy" />
            <img src="/images/usa/colorado/buenavista/mlt.jpg" alt="Midland Tunnel Aerial View" loading="lazy" />
            <img src="/images/usa/colorado/buenavista/d6.jpg" alt="Street" loading="lazy" />
          </div>
        </section>
      </div>

      {/* EMAIL BUTTON */}
      <div className="email-wrapper">
        <button className="email-link" onClick={() => setOpen(true)}>
          contact@thefindingspot.com
        </button>
      </div>

      {/* POPUP */}
      <ContactPopup isOpen={open} onClose={() => setOpen(false)} />

      <Footer />
    </>
  );
}
